import React, { useRef, useState } from 'react';
import { CalendarIcon, HeartIcon } from '@heroicons/react/24/outline';
import SNPCCComplianceError from './SNPCCComplianceError';
import { SCRUBBER_API_BASE } from '../api/config';
import { csrfFetch } from '../api/csrf';

export default function SNPCCFileUploadPanel({
  onFileUpload,
  onValidationComplete,
  selectedTable,
  onEngagementDateChange
}) {
  const fileInputRef = useRef();
  const [engagementDate, setEngagementDate] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [complianceError, setComplianceError] = useState(null);

  // CC-IA (Table 1IA) validates layout and dependency chains only — no
  // engagement-letter date is involved (timeliness lives in SNPE/HRAT-IA).
  const isCCIA = selectedTable?.universeType === 'snpcc_ccia';
  const tableShort = isCCIA ? 'CC-IA (Table 1IA)' : 'SNPE';

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) await processFile(file);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) await processFile(file);
  };

  const handleEngagementDateChange = (e) => {
    const date = e.target.value;
    setEngagementDate(date);
    if (onEngagementDateChange) {
      onEngagementDateChange(date);
    }
  };

  const processFile = async (file) => {
    if (!isCCIA && !engagementDate) {
      alert('Please select the Engagement Letter Date before uploading the file.');
      return;
    }

    setIsValidating(true);
    setComplianceError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (!isCCIA) formData.append('engagementLetterDate', engagementDate);

      console.log('🛠 DEBUG — Uploading SNPCC file:', {
        filename: file.name,
        universeType: selectedTable?.universeType
      });

      const endpoint = isCCIA ? 'validate-ccia' : 'validate-snpe';
      const response = await csrfFetch(`${SCRUBBER_API_BASE}/api/snpcc/${endpoint}`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Validation failed with status ${response.status}`);
      }

      const result = await response.json();
      
      console.log('✅ SNPCC validation response:', result);

      // Check for header compliance errors
      const headerErrors = result.errors?.filter(err => err.row === 'Header') || [];
      if (headerErrors.length > 0) {
        // Extract actual headers from the debug info (if available) or from error context
        setComplianceError({
          headerErrors,
          actualHeaders: null // We'll get this from server debug logs
        });
        return; // Don't proceed with normal flow
      }

      if (onFileUpload) onFileUpload(file);
      if (onValidationComplete) onValidationComplete(result);

    } catch (error) {
      console.error('❌ SNPCC upload failed:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Show compliance error if header validation failed */}
      {complianceError && (
        <SNPCCComplianceError 
          headerErrors={complianceError.headerErrors}
          actualHeaders={complianceError.actualHeaders}
        />
      )}

      {/* Engagement Date Input — not applicable to CC-IA */}
      {!isCCIA && (
      <div className="bg-emerald-50/50 border border-emerald-200/60 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center shadow-lg">
            <CalendarIcon className="w-6 h-6 text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-bold text-emerald-900 mb-2">
              Engagement Letter Date
            </h3>
            <p className="text-emerald-700 text-sm mb-4">
              Enter the audit engagement letter date. This is used to determine HRA timeliness requirements and eligibility windows.
            </p>
            
            <input
              type="date"
              value={engagementDate}
              onChange={handleEngagementDateChange}
              className="
                w-full max-w-xs rounded-xl border border-emerald-300/60 px-4 py-3 text-base
                focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500
                bg-white/80 backdrop-blur-sm transition-all duration-200
                hover:border-emerald-400/60 hover:bg-white/90
              "
              required
            />
          </div>
        </div>
      </div>
      )}

      {/* File Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        className={`
          group relative w-full text-center overflow-hidden
          rounded-3xl p-10 border-2 border-dashed transition-all duration-300 ease-out
          ${isValidating 
            ? 'border-emerald-400/80 bg-emerald-50/50 cursor-wait'
            : 'border-slate-300/60 bg-gradient-to-br from-white/90 via-emerald-50/30 to-teal-50/20 hover:border-emerald-400/80 hover:scale-[1.01] cursor-pointer'
          }
          backdrop-blur-xl shadow-lg hover:shadow-2xl
          before:absolute before:inset-0 before:bg-gradient-to-br before:from-emerald-500/5 before:via-teal-500/5 before:to-amber-500/5 before:opacity-0 before:group-hover:opacity-100 before:transition-opacity before:duration-300 before:rounded-3xl
        `}
      >
        {/* Decorative background elements */}
        <div className="absolute top-6 right-6 w-3 h-3 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full opacity-60 group-hover:opacity-100 transition-all duration-300 group-hover:scale-150"></div>
        <div className="absolute bottom-6 left-6 w-2 h-2 bg-gradient-to-r from-teal-400 to-amber-500 rounded-full opacity-40 group-hover:opacity-80 transition-all duration-300 group-hover:scale-150"></div>
        
        {/* Header with icon */}
        <div className="relative z-10 mb-6">
          <div className="inline-flex items-center gap-3 px-4 py-2 bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-amber-500/20 border border-emerald-300/50 rounded-2xl mb-4">
            <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 via-teal-500 to-amber-500 rounded-full"></div>
            <span className="text-sm font-bold text-emerald-700">{tableShort} File Upload</span>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 space-y-4">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-amber-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
            <HeartIcon className="w-8 h-8 text-white" />
          </div>

          <h3 className="text-2xl font-bold bg-gradient-to-r from-slate-900 via-emerald-800 to-teal-800 bg-clip-text text-transparent mb-2">
            {isCCIA ? 'Upload CC-IA (Table 1IA) File' : 'Upload SNPE Universe File'}
          </h3>

          <p className="text-slate-600 text-base max-w-md mx-auto">
            {isValidating
              ? (isCCIA ? 'Validating CC-IA layout and dependency chains...' : 'Validating SNPE data and evaluating HRA timeliness...')
              : (isCCIA
                ? 'Upload your completed Care Coordination Impact Analysis for full CMS layout validation before submission'
                : 'Upload your SNPE universe file for validation and timeliness analysis')}
          </p>

          {/* Upload button */}
          <button
            onClick={() => !isValidating && fileInputRef.current.click()}
            disabled={isValidating || (!isCCIA && !engagementDate)}
            className={`
              inline-flex items-center justify-center gap-2
              rounded-2xl px-8 py-4 text-base font-bold transition-all duration-300
              focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-2
              ${isValidating 
                ? 'bg-emerald-400 text-white cursor-wait'
                : !engagementDate
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'text-white bg-gradient-to-r from-emerald-600 via-teal-600 to-amber-600 hover:from-emerald-700 hover:via-teal-700 hover:to-amber-700 shadow-lg hover:shadow-xl hover:-translate-y-1 hover:scale-105'
              }
            `}
          >
            {isValidating ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                <span>Validating...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8l-4 4m0 0l-4-4" />
                </svg>
                <span>{isCCIA ? 'Select CC-IA File' : 'Select SNPE File'}</span>
              </>
            )}
          </button>

          {!isCCIA && !engagementDate && (
            <p className="text-amber-600 text-sm font-medium">
              Please select the Engagement Letter Date above before uploading
            </p>
          )}

          {/* File type indicators */}
          <div className="flex items-center justify-center gap-3 mt-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full border border-green-200/50">
              <div className="w-2 h-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"></div>
              <span className="text-xs font-medium text-green-700">Excel (.xlsx)</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-orange-100 to-amber-100 rounded-full border border-orange-200/50">
              <div className="w-2 h-2 bg-gradient-to-r from-orange-500 to-amber-600 rounded-full"></div>
              <span className="text-xs font-medium text-orange-700">Text (.txt)</span>
            </div>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          type="file"
          accept=".xlsx,.txt"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />

        {/* Subtle border glow on hover */}
        <div className="absolute inset-0 rounded-3xl ring-1 ring-transparent group-hover:ring-emerald-300/50 pointer-events-none transition-all duration-300" />
      </div>

      {/* Info section */}
      <div className="bg-slate-50/50 border border-slate-200/60 rounded-xl p-4">
        <div className="text-sm text-slate-700">
          <p className="font-semibold mb-2">{isCCIA ? 'CC-IA Validation includes:' : 'SNPE Validation includes:'}</p>
          {isCCIA ? (
            <ul className="space-y-1 text-slate-600">
              <li>• Exact CMS layout: 23 columns (A-W), exact names and order</li>
              <li>• Schema validation (MBI format, plan types, date formats)</li>
              <li>• Y/N/NA dependency chains (HRA, ICP, hospitalization, ICT gates)</li>
              <li>• ICP date ordering (initial, previous, and most recent revisions)</li>
              <li>• Basis values (Initial, Annual, Change in Status)</li>
            </ul>
          ) : (
            <ul className="space-y-1 text-slate-600">
              <li>• Schema validation (MBI format, plan types, date formats)</li>
              <li>• IHRA timeliness evaluation (±90 days from enrollment)</li>
              <li>• AHRA timeliness evaluation (365-day cycles)</li>
              <li>• Duplicate enrollee detection</li>
              <li>• Automatic HRAT-IA generation for untimely assessments</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
